import { waitForPersistedAuthUidMatch } from './firebaseAuthIdbShared.ts'
import { readMainPersistedAuthUidSafe } from './mainAuthIdb.ts'
import { recordSwAuthSyncPosted } from './swAuthSyncMetrics.ts'
import {
  SW_AUTH_CLEAR_MESSAGE_TYPE,
  SW_AUTH_SYNC_MESSAGE_TYPE,
  type SwAuthClearMessage,
  type SwAuthSyncMessage,
} from './swAuthSyncMessages.ts'

export type SwAuthSyncPayload = {
  uid: string
  issuedAtMs: number
}

export {
  SW_AUTH_CLEAR_MESSAGE_TYPE,
  SW_AUTH_SYNC_MESSAGE_TYPE,
  type SwAuthClearMessage,
  type SwAuthSyncMessage,
  type SwAuthSyncEpoch,
} from './swAuthSyncMessages.ts'

/**
 * Notifies the service worker to reload auth from shared IDB.
 * With payload: IDB uid preflight + epoch metadata for SW mismatch retry.
 * Without payload: cold alignment only (e.g. logged-out SW_ACTIVATED).
 */
export async function notifySwAuthSync(payload?: SwAuthSyncPayload): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  const controller = navigator.serviceWorker.controller
  if (!controller) return

  let preflight = { matched: true, waitMs: 0, hasEpoch: false, preflightReadFailed: false }
  const message: SwAuthSyncMessage = { type: SW_AUTH_SYNC_MESSAGE_TYPE }

  if (payload) {
    let preflightReadFailed = false
    const uidPreflight = await waitForPersistedAuthUidMatch(payload.uid, async () => {
      const result = await readMainPersistedAuthUidSafe()
      if (result.status === 'error') {
        preflightReadFailed = true
        return null
      }
      return result.uid
    })
    preflight = {
      ...uidPreflight,
      hasEpoch: true,
      preflightReadFailed,
    }
    message.uid = payload.uid
    message.issuedAtMs = payload.issuedAtMs
  }

  recordSwAuthSyncPosted(preflight)
  controller.postMessage(message)
}

export function notifySwAuthClear(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  navigator.serviceWorker.controller?.postMessage({
    type: SW_AUTH_CLEAR_MESSAGE_TYPE,
  } satisfies SwAuthClearMessage)
}
